import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ARROW_WIDTH_OFFSET, Carousel } from '../../shared/carousel/Carousel';
import { Icon } from '../../shared/icon/Icon';
import { ChoiceLeft, ChoiceRight, Delete, QuestionMark, Tick, WarningSign } from '../../shared/icons';
import { IconTooltip } from '../../shared/tooltip';
import styles from './NettingEngine.module.scss';
import { NettingEngineAssumptionModal } from './NettingEngineAssumptionModal';
import { NettingEngineJurisdictionResults } from './NettingEngineJurisdictionResults';
import { NettingEngineSectionTenModal } from './NettingEngineSectionTenModal';
import { NettingEngineWhatAreTheseModal } from './NettingEngineWhatAreTheseModal';
import { downloadNettingEngineResultsStarted, getIsCustomConclusionValue, getIsDownloadingNettingEngineSummary, getNettingAssumptions, getNettingAssumptionsModalOpen, NettingResponseData, resetEngineDataState, setNettingAssumptionsModalOpen, toggleIndustryOrCustomConclusions } from './store';
import { NettingEngineSummary } from '../store/types';
import { Button } from '../../shared/button/Button';

const { green, red, amber, darkRed, amethyst, french, yellowGreen, fixedPrimary } = styles;

const RESULT_MARGIN_PADDING_BORDER_OFFSET = 42;
const CAROUSEL_MARKERS_OFFSET = 10;
const CAROUSEL_MARGIN_RIGHT = 10;

const HEIGHT_OFFSET = 116;

interface NettingEngineResultsProps {
    results: NettingResponseData;
    width: number;
    height: number;
    isInstanceView?: boolean;
}

export const NettingEngineResults: React.FC<NettingEngineResultsProps> = ({ results, width, height, isInstanceView = false }) => {
    const { jurisdictionalInformation, overallSummary, overallClientSummary, sectionTenImpactsDecision } = results;

    const dispatch = useAppDispatch();
    const [sectionTenModalOpen, setSectionTenModalOpen] = useState(false);
    const [whatIsThisModalOpen, setWhatIsThisModalOpen] = useState(false);
    const nettingEngineAssumptionsModalOpen = useAppSelector(getNettingAssumptionsModalOpen);
    const nettingEngineAssumptions = useAppSelector(getNettingAssumptions);
    const isCustomConclusion = useAppSelector(getIsCustomConclusionValue);
    const isDownloading = useAppSelector(getIsDownloadingNettingEngineSummary);
    const openNettingAssumptionsModal = useCallback(() => dispatch(setNettingAssumptionsModalOpen(true)), [dispatch]);
    const closeNettingAssumptionsModal = useCallback(() => dispatch(setNettingAssumptionsModalOpen(false)), [dispatch]);
    const openSectionTenModal = useCallback(() => {
        if (sectionTenImpactsDecision) {
            setSectionTenModalOpen(true);
        }
    }, [setSectionTenModalOpen, sectionTenImpactsDecision]);
    const closeSectionTenModal = useCallback(() => setSectionTenModalOpen(false), [setSectionTenModalOpen]);
    const openWhatIsThisModal = useCallback(() => setWhatIsThisModalOpen(true), [setWhatIsThisModalOpen]);
    const closeWhatIsThisModal = useCallback(() => setWhatIsThisModalOpen(false), [setWhatIsThisModalOpen]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const conclusionsButton = useCallback(() => {
        setIsLoading(true);
        // Loading spinner added in for user experience when switching between industry and custom answers. No data is being fetched
        dispatch(toggleIndustryOrCustomConclusions(!isCustomConclusion));
        setTimeout(() => setIsLoading(false), 150);
    }, [dispatch, setIsLoading, isCustomConclusion]);

    const summaryValue = useMemo(() => isCustomConclusion ? overallClientSummary : overallSummary, [isCustomConclusion, overallClientSummary, overallSummary]);
    const showCustomConclusionIcon = useMemo(() => jurisdictionalInformation.some(jurisdictionalResult => !isNull(jurisdictionalResult.clientSignOffDetails)), [jurisdictionalInformation]);

    const iconToShow = useMemo(() => {
        switch (summaryValue) {
            case NettingEngineSummary.YES:
                return { icon: Tick, color: green };
            case NettingEngineSummary.YES_PRODUCTS_UNKNOWN:
                return { icon: Tick, color: yellowGreen };
            case NettingEngineSummary.NO:
                return { icon: Delete, color: red };
            default:
                return { icon: WarningSign, color: amber };
        }
    }, [summaryValue]);

    const markers = useMemo(() => jurisdictionalInformation.map((_, index) => index), [jurisdictionalInformation]);

    const backToTiles = useCallback(() => dispatch(resetEngineDataState()), [dispatch]);

    const conclusionsLabel = useMemo(() => isCustomConclusion ? 'View Ark51 Conclusions' : 'View Custom Conclusions', [isCustomConclusion]);
    const conclusionIcon = useMemo(() => isCustomConclusion ? ChoiceLeft : ChoiceRight, [isCustomConclusion]);
    const whatAreTheseText = useMemo(() => (!showCustomConclusionIcon && !sectionTenImpactsDecision) ? '(What is this?)' : '(What are these?)', [showCustomConclusionIcon, sectionTenImpactsDecision]);

    const carouselHeight = useMemo(() => height - HEIGHT_OFFSET, [height]);
    const resultsWidth = useMemo(() => width - ((RESULT_MARGIN_PADDING_BORDER_OFFSET + jurisdictionalInformation.length > 0 ? (2 * ARROW_WIDTH_OFFSET) : 0) - CAROUSEL_MARGIN_RIGHT), [width, jurisdictionalInformation]);
    const resultsHeight = useMemo(() => carouselHeight - (RESULT_MARGIN_PADDING_BORDER_OFFSET + CAROUSEL_MARKERS_OFFSET), [carouselHeight]);

    const downloadNettingEngine = useCallback(() => {
        const svgHeight = resultsHeight - 30;
        const svgWidth = resultsWidth * 0.45;
        dispatch(downloadNettingEngineResultsStarted(svgHeight, svgWidth));
    }, [dispatch, resultsHeight, resultsWidth]);

    return (
        <div className={styles.nettingResultsWrapper}>
            <div className={styles.titleContainer}>
                <div className={styles.title} data-testid='netting-engine-title'>Netting Engine</div>
                <Button label='Download' onClick={downloadNettingEngine} width='fit-content' showSpinner={isDownloading} />
            </div>
            <div className={styles.nettingOverallSummary}>
                <div className={styles.backButtonWrapper}>
                    {!isInstanceView &&
                        <Link onClick={backToTiles} className={styles.backButton} to='/opinions/netting-engine'>← Back</Link>
                    }
                </div>
                <div className={styles.nettingSummary}>Overall Analysis:
                    <span className={styles.nettingOverallSpan} style={{ color: `${iconToShow.color}` }}>{summaryValue}</span>
                    <Icon icon={iconToShow.icon} fontSize={20} color={iconToShow.color} fill={fixedPrimary} />
                </div>
                <div className={styles.helpIconsWrapper}>
                    {sectionTenImpactsDecision ?
                        <div className={styles.helpIcon} onClick={openSectionTenModal}>
                            <IconTooltip icon={WarningSign} fontSize={28} iconColor={darkRed} content='Section 10(a)' cursor='pointer' placement='left' />
                        </div> :
                        null
                    }
                    {showCustomConclusionIcon ?
                        <div className={styles.helpIcon} onClick={conclusionsButton}>
                            <IconTooltip icon={conclusionIcon} fontSize={28} iconColor={french} content={conclusionsLabel} cursor='pointer' placement='top' />
                        </div> :
                        null
                    }
                    <div className={styles.helpIcon} onClick={openNettingAssumptionsModal}>
                        <IconTooltip icon={QuestionMark} fontSize={28} iconColor={amethyst} content='View Assumptions' cursor='pointer' placement='top' />
                    </div>
                    <div className={styles.whatIsThis} onClick={openWhatIsThisModal}>{whatAreTheseText}</div>
                </div>
            </div>
            <div className={styles.resultsCarouselWrapper}>
                <Carousel
                    markers={markers}
                    height={carouselHeight}
                    width={width - (2 * ARROW_WIDTH_OFFSET)}
                >
                    {jurisdictionalInformation.map((jurisdiction, index) => (
                        <NettingEngineJurisdictionResults key={index} jurisdictionalResults={jurisdiction} viewCustomConclusions={isCustomConclusion} height={resultsHeight} width={resultsWidth} isLoading={isLoading} index={index} isInstanceView={isInstanceView} isSingleJurisdiction={jurisdictionalInformation.length === 1} />
                    ))}
                </Carousel>
            </div>
            <NettingEngineAssumptionModal isOpen={nettingEngineAssumptionsModalOpen} closeModal={closeNettingAssumptionsModal} assumptions={nettingEngineAssumptions} />
            <NettingEngineSectionTenModal isOpen={sectionTenModalOpen} closeModal={closeSectionTenModal} />
            <NettingEngineWhatAreTheseModal isOpen={whatIsThisModalOpen} closeModal={closeWhatIsThisModal} sectionTenImpactsDecision={sectionTenImpactsDecision} showCustomConclusionIcon={showCustomConclusionIcon} />
        </div>
    );
};
